import { useNavigate } from "react-router-dom";

import { ArrowRight, Brain, Eye, Shield, Users, Zap } from "lucide-react";

const REPORTS = {
  audit: {
    icon: Zap,
    label: "AI SEO Audit",
    tagline: "Know exactly what LLMs see",
    description:
      "Get a scored breakdown of your hotel page — uncover what stops ChatGPT, Gemini, and Claude from recommending you.",
    cta: "Run a free audit",
    path: "/",
  },
  visibility: {
    icon: Eye,
    label: "LLM Visibility",
    tagline: "See your AI search ranking",
    description:
      "Find out if your hotel appears when travellers ask AI for recommendations in your destination — and how you rank against competitors.",
    cta: "Check visibility",
    path: "/llm-visibility",
  },
  ota: {
    icon: Shield,
    label: "OTA vs Direct",
    tagline: "Protect your booking margin",
    description:
      "Discover whether AI is sending guests to OTAs or directly to your website — and take action to reclaim commissions.",
    cta: "Analyse OTA exposure",
    path: "/ota-direct",
  },
  persona: {
    icon: Users,
    label: "Persona Visibility",
    tagline: "Reach every traveler segment",
    description:
      "See how visible your hotel is to each traveler persona — couples, families, business, solo — across ChatGPT, Gemini, and Claude.",
    cta: "Scan your personas",
    path: "/persona",
  },
  perception: {
    icon: Brain,
    label: "AI Perception",
    tagline: "See how AI describes you",
    description:
      "Score how ChatGPT, Gemini, and Claude actually describe your hotel — strengths, weaknesses, blind spots — with fixes for every gap.",
    cta: "Check your perception",
    path: "/perception",
  },
};

const CrossReportBanner = ({ currentReport }) => {
  const navigate = useNavigate();
  const others = Object.entries(REPORTS).filter(([key]) => key !== currentReport);

  return (
    <div className="mt-16 border-t border-surface-container-high pt-12">
      <div className="text-center mb-8">
        <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-primary mb-2">
          More insights
        </p>
        <h2 className="text-2xl font-bold text-on-surface">
          Get the full picture of your AI presence
        </h2>
        <p className="text-sm text-on-surface-variant mt-2 max-w-xl mx-auto">
          Each report reveals a different dimension of how AI search affects your hotel. Run them
          together for a complete strategy.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {others.map(([key, report]) => {
          const Icon = report.icon;
          return (
            <div
              key={key}
              className="group flex flex-col gap-4 p-6 rounded-2xl border border-surface-container-high bg-surface-container-lowest hover:border-primary/40 hover:bg-surface-container-low transition-all duration-200"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-0.5">
                    {report.tagline}
                  </p>
                  <h3 className="text-base font-bold text-on-surface">{report.label}</h3>
                  <p className="text-sm text-on-surface-variant mt-1 leading-relaxed">
                    {report.description}
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  navigate(report.path);
                  window.scrollTo({ top: 0 });
                }}
                className="mt-auto self-start flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all duration-150"
              >
                {report.cta}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CrossReportBanner;
